/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { addDays, format, differenceInCalendarDays, eachDayOfInterval } from "date-fns";
import { COLOR, FONT, TEXT, RADIUS, SPACING, SHADOW } from "../utils/theme";
import { Icon } from "../utils/icons";
import { createTripDocument, writeDaysAndStartGeneration } from "../utils/tripCreation";
import { getSetting, getModelTimeout } from "../utils/settings";
import { authenticatedFetch } from "../utils/apiClient";
import OrbitLoader from "./OrbitLoader";
import Toast, { useToast } from "./Toast";

const API_URL = process.env.REACT_APP_PROXY_URL || "http://localhost:3001";

const STYLE_OPTIONS = ["City life", "Adventure", "Foodie", "Culture", "Nature", "Relaxation", "Nightlife"];
const BUDGET_OPTIONS = ["Budget", "Mid-range", "Premium", "Luxury"];

const inputBase = {
  width: "100%",
  padding: "10px 0",
  fontFamily: FONT.serif,
  fontSize: 15,
  color: COLOR.inkDark,
  background: "transparent",
  border: "none",
  borderBottom: `1px solid ${COLOR.borderWarm}`,
  outline: "none",
  transition: "border-color 0.2s",
};

// Branded CSS overrides for react-day-picker v9
const pickerStyles = `
  .rdp-root {
    background-color: ${COLOR.cream};
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.12);
    --rdp-accent-color: ${COLOR.mauve};
    --rdp-accent-background-color: ${COLOR.mauve};
  }
  .rdp-selected .rdp-day_button,
  .rdp-selected .rdp-day_button:hover {
    background-color: ${COLOR.mauve};
    color: ${COLOR.white};
  }
  .rdp-range_middle {
    background-color: ${COLOR.mauve}22;
    color: ${COLOR.inkDark};
  }
  .rdp-range_middle .rdp-day_button {
    color: ${COLOR.inkDark};
    background: transparent;
  }
  .rdp-range_start .rdp-day_button,
  .rdp-range_end .rdp-day_button {
    background-color: ${COLOR.mauve};
    color: ${COLOR.white};
  }
  .rdp-day_button:hover:not([disabled]) {
    background-color: ${COLOR.mauve}15;
  }
  .rdp-caption_label {
    color: ${COLOR.inkDark};
    font-family: ${FONT.serif};
  }
  .rdp-day {
    color: ${COLOR.inkDark};
    font-family: ${FONT.serif};
  }
  .rdp-weekday {
    color: ${COLOR.inkMid};
    font-family: ${FONT.mono};
    font-size: 10px;
  }
  .rdp-disabled {
    color: ${COLOR.inkLight} !important;
    opacity: 0.4;
  }
  .rdp-chevron {
    fill: ${COLOR.mauve};
  }
  .rdp-today:not(.rdp-selected) .rdp-day_button {
    border: 1px solid ${COLOR.mauve};
    border-radius: 8px;
  }
`;

export default function TripCreationModal() {
  const navigate = useNavigate();

  const [destination, setDestination] = useState("");
  const [departureCity, setDepartureCity] = useState("Chicago");
  const [startDate, setStartDate] = useState(null);   // Date | null
  const [endDate, setEndDate] = useState(null);         // Date | null
  const [travelers, setTravelers] = useState(2);
  const [showMore, setShowMore] = useState(false);
  const [styles, setStyles] = useState([]);
  const [budget, setBudget] = useState("");
  const [mustSees, setMustSees] = useState("");
  const [avoid, setAvoid] = useState("");
  const [travelerNames, setTravelerNames] = useState("");
  const [isMultiCity, setIsMultiCity] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [toast, showToast] = useToast();

  // Picker open state
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // Validation note for auto-corrected end date
  const [dateNote, setDateNote] = useState("");

  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);
  const isMountedRef = useRef(true);
  const controllerRef = useRef(null);

  // Cleanup: abort in-flight fetch on unmount (no toast for unmount aborts)
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, []);

  // Click-outside handler
  useEffect(() => {
    function handleClick(e) {
      if (startOpen && startPickerRef.current && !startPickerRef.current.contains(e.target)) {
        setStartOpen(false);
      }
      if (endOpen && endPickerRef.current && !endPickerRef.current.contains(e.target)) {
        setEndOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [startOpen, endOpen]);

  // Auto-clear date note after 2s
  useEffect(() => {
    if (!dateNote) return;
    const t = setTimeout(() => setDateNote(""), 2000);
    return () => clearTimeout(t);
  }, [dateNote]);

  const canSubmit =
    destination.trim() &&
    startDate instanceof Date &&
    endDate instanceof Date &&
    differenceInCalendarDays(endDate, startDate) >= 1 &&
    !submitting;

  const tripDays =
    startDate instanceof Date && endDate instanceof Date
      ? differenceInCalendarDays(endDate, startDate) + 1
      : null;

  const toggleStyle = (s) =>
    setStyles((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  const handleStartSelect = (date) => {
    if (!date) return;
    setStartDate(date);
    setStartOpen(false);
    // Auto-set end date if missing or invalid
    if (!endDate || endDate <= date) {
      setEndDate(addDays(date, 7));
      if (endDate && endDate <= date) {
        setDateNote("End date adjusted to be after start date");
      }
    }
    // Open end picker automatically
    setTimeout(() => setEndOpen(true), 150);
  };

  const handleEndSelect = (date) => {
    if (!date) return;
    // Guard: if somehow before start, auto-correct
    if (startDate && date <= startDate) {
      const corrected = addDays(startDate, 1);
      setEndDate(corrected);
      setDateNote("End date adjusted to be after start date");
    } else {
      setEndDate(date);
    }
    setEndOpen(false);
  };

  // Range highlight modifiers for end picker
  const rangeModifiers =
    startDate instanceof Date && endDate instanceof Date && endDate > startDate
      ? {
          range_start: startDate,
          range_end: endDate,
          range_middle: eachDayOfInterval({ start: addDays(startDate, 1), end: addDays(endDate, -1) }),
        }
      : {};

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");

    const brief = {
      title: destination.trim(),
      travelers: Array.from({ length: travelers }, (_, i) => `Traveler ${i + 1}`),
      style: styles,
      budget: budget || "Mid-range",
      departureCity: departureCity.trim() || "Chicago",
      departureDate: format(startDate, "yyyy-MM-dd"),
      returnDate: format(endDate, "yyyy-MM-dd"),
      destinations: [destination.trim()],
      mustSees: mustSees.trim() ? mustSees.split(",").map((s) => s.trim()).filter(Boolean) : [],
      avoid: avoid.trim() ? avoid.split(",").map((s) => s.trim()).filter(Boolean) : [],
      travelerNames: travelerNames.trim() ? travelerNames.split(",").map((s) => s.trim()).filter(Boolean) : [],
      isMultiCity,
    };

    try {
      const slug = await createTripDocument(brief);

      // Multi-city: wizard flow via TripSkeletonApproval
      if (isMultiCity) {
        navigate(`/trip/${slug}`);
        return;
      }

      // Fast path: generate skeleton inline, then write days + start generation
      setGenerating(true);

      const controller = new AbortController();
      controllerRef.current = controller;
      const timeout = setTimeout(() => controller.abort(), getModelTimeout('skeleton'));

      try {
        const res = await authenticatedFetch(`${API_URL}/api/generate-trip-skeleton`, {
          method: "POST",
          signal: controller.signal,
          body: JSON.stringify({
            destination: brief.destinations?.join(", ") || brief.title,
            departureCity: brief.departureCity || "",
            startDate: brief.departureDate,
            endDate: brief.returnDate,
            travelers: brief.travelers?.join(", ") || "",
            style: brief.style || "",
            budget: brief.budget || "",
            mustSees: brief.mustSees?.join(", ") || "",
            avoid: brief.avoid?.join(", ") || "",
            extendedCaching: getSetting("extendedCaching"),
            language: getSetting('wanderologie_settings.language') || 'English',
            model: getSetting('wanderologie_settings.aiModel'),
          }),
        });

        clearTimeout(timeout);

        if (res.status === 429) {
          const errData = await res.json();
          showToast(errData.message, "error");
          return;
        }

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error?.message || `Server error (${res.status})`);
        }

        await writeDaysAndStartGeneration({
          slug,
          skeleton: { tripTitle: data.tripTitle, days: data.days },
          brief,
          apiUrl: API_URL,
          wave2Days: null,
          skipWizard: true,
        });

        navigate(`/trip/${slug}`);
      } catch (fetchErr) {
        clearTimeout(timeout);
        controllerRef.current = null;
        console.error("[TripCreationModal] Fast path failed:", fetchErr);
        // Only show toast + navigate if still mounted (skip for unmount aborts)
        if (isMountedRef.current) {
          const toastMsg = fetchErr.name === "AbortError"
            ? "Trip is still generating — select it from your dashboard to check progress"
            : "Trip creation hit a snag — select the trip from your dashboard to continue";
          showToast(toastMsg, fetchErr.name === "AbortError" ? "success" : "error");
          setGenerating(false);
          setSubmitting(false);
          setTimeout(() => navigate("/"), 1800);
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
      setSubmitting(false);
      setGenerating(false);
    }
  };

  const today = new Date();
  const defaultStartMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9000,
      height: "100dvh", maxHeight: "100dvh",
      background: COLOR.cream,
      overflowY: "auto", WebkitOverflowScrolling: "touch",
    }}>
      <style>{pickerStyles}</style>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      {/* Generating transition screen */}
      {generating && (
        <div data-testid="creation-generating" style={{
          position: "absolute", inset: 0, zIndex: 9002,
          background: COLOR.cream,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 24,
        }}>
          <OrbitLoader />
          <p style={{
            fontFamily: FONT.display, fontSize: 20, fontWeight: 400,
            color: COLOR.inkDark, textAlign: "center",
          }}>
            Generating your trip...
          </p>
          <p style={{ fontFamily: FONT.serif, fontSize: 13, color: COLOR.inkMid }}>
            This usually takes about 10 seconds
          </p>
        </div>
      )}

      {/* Close button */}
      <button
        onClick={() => navigate("/")}
        style={{
          position: "fixed", top: 16, right: 16, zIndex: 9001,
          width: 40, height: 40, borderRadius: RADIUS.pill,
          background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <Icon name="close" size={18} color={COLOR.inkMid} />
      </button>

      {!generating && <div style={{
        maxWidth: 540, margin: "0 auto",
        padding: `64px ${SPACING.pagePadding} 120px`,
      }}>
        {/* Header */}
        <h1 style={{
          fontFamily: FONT.display, fontSize: 32, fontWeight: 400,
          color: COLOR.inkDark, margin: "0 0 8px", lineHeight: 1.2,
        }}>
          Plan a new trip
        </h1>
        <p style={{ ...TEXT.detail, margin: `0 0 ${SPACING.sectionGap}` }}>
          Tell us where you're headed and we'll build your itinerary.
        </p>

        {/* SECTION 1 — Where are you going? */}
        <div style={{ marginBottom: SPACING.sectionGap }}>
          <h2 style={{
            ...TEXT.label, margin: "0 0 20px",
          }}>
            Where are you going?
          </h2>

          <Field label="Destination">
            <input
              type="text"
              data-testid="creation-destination"
              placeholder="e.g. Australia, Southeast Asia, Italy"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              style={inputBase}
            />
          </Field>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid="multi-city-checkbox"
                checked={isMultiCity}
                onChange={(e) => setIsMultiCity(e.target.checked)}
                style={{ accentColor: COLOR.mauve, width: 16, height: 16 }}
              />
              <span style={{ fontFamily: FONT.serif, fontSize: 14, color: COLOR.inkDark }}>
                Multi-city trip
              </span>
            </label>
            <p style={{
              fontFamily: FONT.serif, fontSize: 11, color: COLOR.inkLight,
              margin: "4px 0 0 24px", lineHeight: 1.4,
            }}>
              Plan across multiple destinations with a step-by-step wizard
            </p>
          </div>

          <Field label="Departure city">
            <input
              type="text"
              value={departureCity}
              onChange={(e) => setDepartureCity(e.target.value)}
              style={inputBase}
            />
          </Field>

          <div style={{ display: "flex", gap: 20 }}>
            {/* Start date picker */}
            <Field label="Start date" style={{ flex: 1 }}>
              <div ref={startPickerRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  data-testid="start-date-trigger"
                  onClick={() => { setStartOpen(!startOpen); setEndOpen(false); }}
                  style={{
                    ...inputBase,
                    fontFamily: FONT.mono, fontSize: 13,
                    textAlign: "left", cursor: "pointer",
                    color: startDate ? COLOR.inkDark : COLOR.inkLight,
                    display: "block", width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {startDate ? format(startDate, "MMM d, yyyy") : "Select date"}
                </button>
                {startOpen && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 100,
                    marginTop: 4,
                  }}>
                    <DayPicker
                      mode="single"
                      selected={startDate}
                      onSelect={handleStartSelect}
                      defaultMonth={startDate || defaultStartMonth}
                      disabled={{ before: today }}
                    />
                  </div>
                )}
              </div>
            </Field>

            {/* End date picker */}
            <Field label="End date" style={{ flex: 1 }}>
              <div ref={endPickerRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  data-testid="end-date-trigger"
                  onClick={() => { setEndOpen(!endOpen); setStartOpen(false); }}
                  style={{
                    ...inputBase,
                    fontFamily: FONT.mono, fontSize: 13,
                    textAlign: "left", cursor: "pointer",
                    color: endDate ? COLOR.inkDark : COLOR.inkLight,
                    display: "block", width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {endDate ? format(endDate, "MMM d, yyyy") : "Select date"}
                </button>
                {endOpen && (
                  <div style={{
                    position: "absolute", top: "100%", right: 0, zIndex: 100,
                    marginTop: 4,
                  }}>
                    <DayPicker
                      mode="single"
                      selected={endDate}
                      onSelect={handleEndSelect}
                      defaultMonth={startDate || defaultStartMonth}
                      disabled={{ before: startDate || today }}
                      modifiers={rangeModifiers}
                      modifiersClassNames={{
                        range_start: "rdp-range_start",
                        range_end: "rdp-range_end",
                        range_middle: "rdp-range_middle",
                      }}
                      footer={tripDays != null && (
                        <div style={{
                          textAlign: "center", paddingTop: 8,
                          fontFamily: FONT.mono, fontSize: 11,
                          color: COLOR.inkMid,
                        }}>
                          {tripDays} day{tripDays !== 1 ? "s" : ""}
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            </Field>
          </div>

          {/* Date validation note */}
          {dateNote && (
            <div style={{
              fontFamily: FONT.serif, fontSize: 11,
              color: COLOR.mauveDark, marginTop: -16, marginBottom: 8,
            }}>
              {dateNote}
            </div>
          )}

          <Field label="Travelers">
            <div data-testid="creation-travelers-count" style={{ display: "flex", alignItems: "center", gap: 16, paddingTop: 6 }}>
              <StepperButton onClick={() => setTravelers((v) => Math.max(1, v - 1))} label="−" />
              <span style={{ fontFamily: FONT.mono, fontSize: 18, color: COLOR.inkDark, minWidth: 24, textAlign: "center" }}>
                {travelers}
              </span>
              <StepperButton onClick={() => setTravelers((v) => Math.min(10, v + 1))} label="+" />
            </div>
          </Field>

          {travelers > 0 && (
            <Field label="Who's going?">
              <input
                type="text"
                data-testid="creation-traveler-names"
                placeholder="e.g. Zach, Mike, Sarah"
                value={travelerNames}
                onChange={(e) => setTravelerNames(e.target.value)}
                style={{ ...inputBase, fontSize: 14, color: COLOR.inkDark }}
              />
            </Field>
          )}
        </div>

        {/* SECTION 2 — Tell us more (expandable) */}
        {!showMore ? (
          <button
            onClick={() => setShowMore(true)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: FONT.serif, fontSize: 14, color: COLOR.mauve,
              padding: 0, marginBottom: SPACING.sectionGap,
            }}
          >
            Add preferences ›
          </button>
        ) : (
          <div style={{ marginBottom: SPACING.sectionGap }}>
            <h2 style={{ ...TEXT.label, margin: "0 0 20px" }}>
              Tell us more
            </h2>

            <Field label="Travel style">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 8 }}>
                {STYLE_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => toggleStyle(s)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: RADIUS.pill,
                      border: `1px solid ${styles.includes(s) ? COLOR.mauve : COLOR.borderWarm}`,
                      background: styles.includes(s) ? COLOR.blush : COLOR.white,
                      color: styles.includes(s) ? COLOR.mauveDark : COLOR.inkMid,
                      fontFamily: FONT.serif, fontSize: 13,
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Budget">
              <div data-testid="creation-budget" style={{ display: "flex", gap: 0, paddingTop: 8, borderRadius: RADIUS.md, overflow: "hidden", border: `1px solid ${COLOR.borderWarm}` }}>
                {BUDGET_OPTIONS.map((b, i) => (
                  <button
                    key={b}
                    onClick={() => setBudget(b)}
                    style={{
                      flex: 1, padding: "8px 4px",
                      background: budget === b ? COLOR.mauve : COLOR.white,
                      color: budget === b ? COLOR.white : COLOR.inkMid,
                      border: "none",
                      borderRight: i < BUDGET_OPTIONS.length - 1 ? `1px solid ${COLOR.borderWarm}` : "none",
                      fontFamily: FONT.mono, fontSize: 10,
                      letterSpacing: "0.05em", textTransform: "uppercase",
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Must-sees">
              <input
                type="text"
                data-testid="creation-must-sees"
                placeholder="e.g. Great Barrier Reef, Sydney Opera House"
                value={mustSees}
                onChange={(e) => setMustSees(e.target.value)}
                style={inputBase}
              />
            </Field>

            <Field label="Avoid">
              <input
                type="text"
                data-testid="creation-avoid"
                placeholder="e.g. long bus rides, tourist traps"
                value={avoid}
                onChange={(e) => setAvoid(e.target.value)}
                style={inputBase}
              />
            </Field>
          </div>
        )}

        {/* Error */}
        {error && (
          <p style={{ ...TEXT.detail, color: COLOR.mauveDark, marginBottom: 16 }}>{error}</p>
        )}

        {/* Submit */}
        <button
          data-testid="creation-submit"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            width: "100%", padding: "16px",
            background: canSubmit ? COLOR.mauve : COLOR.buttonSecondary,
            color: canSubmit ? COLOR.white : COLOR.inkLight,
            border: "none", borderRadius: RADIUS.lg,
            fontFamily: FONT.display, fontSize: 20, fontWeight: 500,
            cursor: canSubmit ? "pointer" : "default",
            opacity: submitting ? 0.7 : 1,
            transition: "all 0.2s",
            boxShadow: canSubmit ? SHADOW.card : "none",
          }}
        >
          {submitting ? "Creating…" : "Generate Trip ✦"}
        </button>
      </div>}
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────── */

function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 24, ...style }}>
      <label style={{ ...TEXT.label, display: "block", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function StepperButton({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: RADIUS.pill,
        background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
        fontFamily: FONT.mono, fontSize: 18, color: COLOR.inkDark,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
